// Configuración · listas de precios y escalas (docs/108 B10). Una lista se crea para una o varias
// empresas; sus precios son VERSIONES con vigencia (un precio nuevo reemplaza al anterior desde su
// fecha, nunca se edita). Las escalas (desde cierta cantidad, otro precio) nacen con el precio. La
// lista elegida se trabaja en la grilla del tratamiento de listas (precios/TratamientoListas.tsx).
import { api } from '../api/cliente';
import { Mantenedor, type DefMantenedor } from '../comun/Mantenedor';
import { TratamientoListas } from '../precios/TratamientoListas';
import { useSesion } from '../sesion/SesionContexto';

interface Lista {
  id: string;
  codigo: string;
  nombre: string;
  uso: string;
  moneda: string;
  semantica: string;
  politica_redondeo: string;
  decimales: number;
  activa: boolean;
  empresa_id: string;
}
export function ListasPrecio({ foco }: { foco?: 'escalas' | undefined }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  return (
    <>
      {foco === 'escalas' && (
        <p className="apagado pantalla">
          Las escalas pertenecen a cada precio: elige una lista, luego el precio, y verás sus
          tramos; un precio nuevo se crea con sus escalas.
        </p>
      )}
      <Mantenedor
        def={
          {
            etiqueta: 'Listas de precios',
            claveConsulta: ['config', 'listas', empresaId],
            listar: async () => {
              const filas = await api.get<Lista[]>('/precios/listas');
              const vistas = new Map<string, Lista>();
              for (const f of filas)
                if (f.empresa_id === empresaId || !vistas.has(f.id)) vistas.set(f.id, f);
              return [...vistas.values()].filter((l) =>
                filas.some((x) => x.id === l.id && x.empresa_id === empresaId),
              );
            },
            clave: (l) => l.id,
            textoDe: (l) => `${l.codigo} ${l.nombre}`,
            columnas: [
              {
                id: 'c',
                titulo: 'Código',
                celda: (l) => <span className="mono">{l.codigo}</span>,
                ancho: '8rem',
              },
              { id: 'n', titulo: 'Nombre', celda: (l) => l.nombre },
              { id: 'u', titulo: 'Uso', celda: (l) => l.uso, ancho: '6rem' },
              {
                id: 's',
                titulo: 'Precios',
                celda: (l) => (l.semantica === 'bruto' ? 'con IVA (bruto)' : 'netos'),
                ancho: '9rem',
              },
              { id: 'a', titulo: 'Activa', celda: (l) => (l.activa ? 'sí' : 'no'), ancho: '5rem' },
            ],
            campos: [
              {
                id: 'codigo',
                etiqueta: 'Código',
                requerido: true,
                soloCrear: true,
                patron: /^[A-Z0-9_-]{1,20}$/,
              },
              { id: 'nombre', etiqueta: 'Nombre', requerido: true },
              {
                id: 'semantica',
                etiqueta: 'Los precios son',
                tipo: 'seleccion',
                requerido: true,
                opciones: [
                  { valor: 'bruto', texto: 'Brutos (con IVA)' },
                  { valor: 'neto', texto: 'Netos (sin IVA)' },
                ],
              },
              {
                id: 'uso',
                etiqueta: 'Uso',
                tipo: 'seleccion',
                opciones: ['venta', 'costo', 'interna'].map((x) => ({ valor: x, texto: x })),
              },
            ],
            crear: (v, motivo) => ({
              ruta: '/precios/listas',
              cuerpo: {
                codigo: v['codigo'],
                nombre: v['nombre'],
                semantica: v['semantica'],
                ...(v['uso'] ? { uso: v['uso'] } : {}),
                // Una lista bruta en Chile incluye el IVA.
                ...(v['semantica'] === 'bruto' ? { impuestosIncluidos: ['IVA'] } : {}),
                empresaIds: [empresaId],
                motivo,
              },
            }),
            conMotivo: true,
            permisoCrear: 'precios.listas.gestionar',
            // La lista elegida se trabaja en la misma grilla del «tratamiento de listas».
            detalle: (l) => <TratamientoListas key={l.id} listaInicial={l.id} listaFija />,
          } satisfies DefMantenedor<Lista>
        }
      />
    </>
  );
}
